/**
 * Recovery-code management for an enrolled user (profile security card).
 *
 * Shows how many single-use codes remain and lets the user regenerate the
 * set. Regeneration invalidates the old codes and requires a fresh TOTP
 * step-up code; the new set is revealed exactly once.
 */
import { useCallback, useState } from 'react';
import authService from '../../services/auth.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import RecoveryCodesPanel from './RecoveryCodesPanel';
import { colors } from '../../styles/theme';

interface Props {
  /** Codes remaining as last reported by /auth/mfa/status. */
  readonly remaining: number | null;
  /** Called after a successful regenerate so the parent can refresh status. */
  readonly onRegenerated: () => void;
}

type Mode = 'idle' | 'confirming' | 'revealed';

export default function RecoveryCodesManager({ remaining, onRegenerated }: Props): JSX.Element {
  const [mode, setMode] = useState<Mode>('idle');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const regenerate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await authService.regenerateRecoveryCodes(code.trim());
      setCodes(res.recovery_codes);
      setMode('revealed');
      setCode('');
      onRegenerated();
    } catch (e: unknown) {
      if (getApiErrorCode(e) === 'mfa_not_enrolled') {
        setError('Turn on two-factor authentication first.');
      } else {
        setError(getApiErrorMessage(e, 'Could not regenerate recovery codes.'));
      }
    } finally {
      setBusy(false);
    }
  }, [code, onRegenerated]);

  const codeNoun = remaining === 1 ? 'code' : 'codes';
  const remainingText =
    remaining === null
      ? 'One-time codes to sign in if you lose your authenticator.'
      : `${remaining} recovery ${codeNoun} remaining. Regenerating replaces the whole set.`;

  return (
    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
      <div className="text-sm font-medium mb-1" style={{ color: colors.ink }}>
        Recovery codes
      </div>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>
        {remainingText}
      </p>

      {error && (
        <div className="rounded-lg p-3 mb-3 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {mode === 'idle' && (
        <button
          type="button"
          onClick={() => { setError(null); setMode('confirming'); }}
          className="text-sm px-3 py-1.5 rounded-lg font-semibold"
          style={{ background: colors.canvas, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Regenerate recovery codes
        </button>
      )}

      {mode === 'confirming' && (
        <div>
          <p className="text-xs mb-2" style={{ color: colors.muted }}>
            Enter a current 6-digit code from your authenticator to confirm.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="6-digit code"
              className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
              style={{ background: colors.surface, color: colors.ink, border: `1px solid ${colors.border}` }}
            />
            <button
              type="button"
              onClick={() => { regenerate(); }}
              disabled={busy || code.trim().length < 6}
              className="text-sm px-3 py-2 rounded-lg font-semibold"
              style={{ background: colors.gold, color: colors.navy }}
            >
              {busy ? 'Working…' : 'Regenerate'}
            </button>
            <button
              type="button"
              onClick={() => { setMode('idle'); setCode(''); setError(null); }}
              disabled={busy}
              className="text-sm px-3 py-2 rounded-lg"
              style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === 'revealed' && (
        <div>
          <RecoveryCodesPanel codes={codes} />
          <button
            type="button"
            onClick={() => { setMode('idle'); setCodes([]); }}
            className="mt-3 text-sm px-3 py-1.5 rounded-lg font-semibold"
            style={{ background: colors.canvas, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
